import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  AuthenticationService,
  ContratoService,
  GrupoEconomicoService,
  PerfilService,
  UsuarioService
} from 'src/app/services';
import { ListaPaginada } from 'src/app/models';
import {
  IContrato,
  IGrupoEconomico,
  IPerfil,
  IPostUsuarioBody,
  ITipoUsuario
} from 'src/app/interfaces';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ECompany, ETipo } from 'src/app/enums';
import { Subscription } from 'rxjs';
import { ModalGeneralComponent } from '../modal-general/modal-general.component';
import { IToken } from 'src/app/interfaces/token/IToken';
import { ENVIRONMENT } from 'src/app/constants';
import { Environment } from 'src/app/types';

@Component({
  selector: 'app-modal-cadastrar-usuario',
  templateUrl: './modal-cadastrar-usuario.component.html',
  styleUrls: ['./modal-cadastrar-usuario.component.scss']
})
export class ModalCadastrarUsuarioComponent implements OnInit, OnDestroy {
  @Output() registerUser = new EventEmitter();
  @ViewChild('ModalCadastrarUsuario') template: TemplateRef<any>;
  @ViewChild(ModalGeneralComponent) modalGeneral: ModalGeneralComponent;
  subscriptions$ = new Subscription();
  formCadastraUsuario!: FormGroup;
  tipos!: ITipoUsuario[];
  gruposEconomicos = new ListaPaginada<IGrupoEconomico>();
  contratos = new ListaPaginada<IContrato>();
  perfis = new ListaPaginada<IPerfil>();
  quantidadePassos = 2;
  passo: number = 1;
  textoBotao: string = 'Continuar';
  token!: IToken;
  tipoUsuarioSendoCadastrado!: ETipo;
  perfilSelecionado: IPerfil;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private grupoEconomicoService: GrupoEconomicoService,
    private contratoService: ContratoService,
    private perfilService: PerfilService,
    private usuarioService: UsuarioService,
    private authenticationService: AuthenticationService,
    @Inject(ENVIRONMENT) public environment: Environment
  ) {}

  exibirOpcaoSelectContrato(env: Environment) {
    return (valor: IContrato) => {
      if (env.company === ECompany.APNETWORKS) {
        return `${valor.codContrato} - ${valor.nomeCliente || valor.nomeFantasiaCliente || 'Sem dados'
          }`;
      }
      if (env.company === ECompany.KM_DO_BRASIL) {
        return `${valor.codContrato} - ${valor.nomeFantasiaCliente || valor.nomeCliente || 'Sem dados'
          }`;
      }
      return 'Sem dados';
    };
  }

  valorOpcaoSelectContrato(valor: IContrato) {
    return valor.codContrato;
  }

  getPerfis() {
    this.subscriptions$.add(
      this.perfilService
        .getPerfil({
          idUsuario: this.token.id,
          codGrupoEconomico:
            this.formCadastraUsuario.get('codGrupoEconomico')?.value,
          perfilInterno: this.tipoUsuarioSendoCadastrado === ETipo.INTERNO,
          paginar: false
        })
        .subscribe({
          next: ({ data }) => {
            this.perfis = data;
          }
        })
    );
  }

  getContratos() {
    this.subscriptions$.add(
      this.contratoService
        .getContratos({
          idUsuario: this.token.id,
          codGrupoEconomico:
            this.formCadastraUsuario.get('codGrupoEconomico')?.value,
          codContrato: this.token.codContrato,
          paginar: false,
          contratoAtivo: true
        })
        .subscribe({
          next: ({ data }) => {
            this.contratos = data;
          }
        })
    );
  }

  getTipos() {
    this.tipos = [
      { tipo: 'Externo', siglaTipo: ETipo.EXTERNO },
      { tipo: 'Interno', siglaTipo: ETipo.INTERNO }
    ];
  }

  getGruposEconomicos() {
    this.subscriptions$.add(
      this.grupoEconomicoService
        .getGrupoEconomicos({
          idUsuario: this.token.id,
          codGrupoEconomico: this.token.codGrupoEconomico,
          paginar: false
        })
        .subscribe({
          next: ({ data }) => {
            this.gruposEconomicos = data;
          }
        })
    );
  }

  realizarRequisicoes() {
    this.getTipos();
    this.getGruposEconomicos();
    this.getPerfis();
    this.getContratos();
  }

  private cadastrarUsuario() {
    const { codGrupoEconomico, associarContrato, ...rest } =
      this.formCadastraUsuario.getRawValue();
    const body: IPostUsuarioBody = {
      codGrupoEconomico: '',
      ...rest
    };
    if (!associarContrato) {
      body.codGrupoEconomico = codGrupoEconomico;
    }

    this.subscriptions$.add(
      this.usuarioService.postUsuario(body).subscribe({
        complete: () => {
          this.registerUser.emit();
          this.fecharModal();
        }
      })
    );
  }
  prosseguir() {
    if (this.passo === this.quantidadePassos) {
      this.cadastrarUsuario();
      return;
    }

    this.realizarRequisicoes();
    this.formCadastraUsuario.patchValue({ passo: this.passo + 1 });
  }

  listarPermissoes() {
    this.modalGeneral.openModal({ size: 'sm' });
  }

  private inicializarFormulario() {
    this.formCadastraUsuario = this.fb.group(
      {
        idUsuario: [this.token.id, Validators.required],
        tipoUsuario: ['', [Validators.required]],
        idPerfil: [0],
        perfil: [null],
        nome: [''],
        email: ['', [Validators.email]],
        telefone: [''],
        celular: [''],
        codGrupoEconomico: [''],
        codContrato: [[]],
        associarContrato: [false],
        passo: [1],
        quantidadePassos: [2]
      },
      {
        validators: [
          (control: AbstractControl) => {
            const {
              tipoUsuario,
              perfil,
              nome,
              email,
              telefone,
              celular,
              codGrupoEconomico,
              codContrato,
              associarContrato,
              passo,
              quantidadePassos
            } = (control as FormGroup).getRawValue();
            if (passo === quantidadePassos && (!perfil || !nome || !email)) {
              return { cadastroUsuarioInvalido: true };
            }

            if (
              passo === quantidadePassos &&
              tipoUsuario === ETipo.EXTERNO &&
              !telefone &&
              !celular
            ) {
              return { cadastroUsuarioExternoInvalido: true };
            }

            if (
              tipoUsuario === ETipo.EXTERNO &&
              ((associarContrato && codContrato.length === 0) ||
                (!associarContrato && !codGrupoEconomico))
            ) {
              return { cadastroUsuarioExternoInvalido: true };
            }

            return null;
          }
        ]
      }
    );

    this.subscriptions$.add(
      this.formCadastraUsuario.get('tipoUsuario')?.valueChanges.subscribe({
        next: (value) => {
          this.tipoUsuarioSendoCadastrado = value;
          this.formCadastraUsuario.patchValue({
            idPerfil: 0,
            perfil: null,
            nome: '',
            email: '',
            telefone: '',
            celular: '',
            codGrupoEconomico: '',
            codContrato: [],
            associarContrato: false
          });
        }
      })
    );

    this.subscriptions$.add(
      this.formCadastraUsuario.get('passo')?.valueChanges.subscribe({
        next: (value) => {
          this.passo = value;
        }
      })
    );

    this.subscriptions$.add(
      this.formCadastraUsuario.get('quantidadePassos')?.valueChanges.subscribe({
        next: (value) => {
          this.quantidadePassos = value;
        }
      })
    );

    this.subscriptions$.add(
      this.formCadastraUsuario.get('perfil')?.valueChanges.subscribe({
        next: (value) => {
          this.perfilSelecionado = value;
          if (!value) {
            return;
          }
          this.formCadastraUsuario.patchValue({ idPerfil: value.id });
        }
      })
    );

    this.subscriptions$.add(
      this.formCadastraUsuario.get('associarContrato')?.valueChanges.subscribe({
        next: (value) => {
          this.formCadastraUsuario.patchValue({ codContrato: [] });
          if (value) {
            this.formCadastraUsuario.get('codGrupoEconomico')?.disable();
            this.realizarRequisicoes();
            return;
          }

          if (this.token.tipoUsuario === ETipo.INTERNO) {
            this.formCadastraUsuario.get('codGrupoEconomico')?.enable();
            return;
          }
        }
      })
    );

    if (this.token.tipoUsuario === ETipo.EXTERNO) {
      this.formCadastraUsuario.patchValue({
        idUsuario: this.token.id,
        tipoUsuario: ETipo.EXTERNO,
        codGrupoEconomico: this.token.codGrupoEconomico
      });
      this.formCadastraUsuario.get('codGrupoEconomico')?.disable();
    }

    if (
      this.token.tipoUsuario === ETipo.EXTERNO &&
      this.token.codContrato.length > 0
    ) {
      this.formCadastraUsuario.patchValue({
        associarContrato: true
      });
    }
  }

  reiniciarPropriedades() {
    this.subscriptions$ = new Subscription();
    this.tipos = undefined! as ITipoUsuario[];
    this.gruposEconomicos = new ListaPaginada<IGrupoEconomico>();
    this.contratos = new ListaPaginada<IContrato>();
    this.perfis = new ListaPaginada<IPerfil>();
    this.quantidadePassos = 2;
    this.passo = 1;
    this.textoBotao = 'Continuar';
    this.tipoUsuarioSendoCadastrado = undefined! as ETipo;
    this.perfilSelecionado = undefined! as IPerfil;
  }

  ngOnInit() {
    this.token = this.authenticationService.obterToken();
    this.inicializarFormulario();
    this.realizarRequisicoes();
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
    this.reiniciarPropriedades();
  }

  abrirModal() {
    this.ngOnInit();
    this.modalService.open(this.template, {
      centered: true,
      size: 'lg',
      beforeDismiss: () => {
        this.ngOnDestroy();
        return true;
      }
    });
  }

  fecharModal() {
    this.modalService.dismissAll();
  }
}
